/**
 * Mordeczka.eu Admin - Book Form Page
 */

import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Spin,
  message,
  Divider,
  Modal,
  Upload,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  worksApi,
  languagesApi,
  literaryGenresApi,
  authorsApi,
  publishersApi,
  imagesApi,
  nationalitiesApi,
  type BookInfoResponse,
} from "../services/api";
import type { WorkCreate, WorkUpdate } from "../types";
import { AIBookHelper } from "../components/AIBookHelper";
import { WorkImagesManager } from "../components/WorkImagesManager";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const { TextArea } = Input;

export function BookFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [authorForm] = Form.useForm();
  const [publisherForm] = Form.useForm();
  const isEditing = Boolean(id);
  const { canCreate, canEdit } = usePermissions();

  // Determine if form is read-only based on permissions
  const isReadOnly = isEditing ? !canEdit : !canCreate;

  // Modal states
  const [isAuthorModalOpen, setIsAuthorModalOpen] = useState(false);
  const [isPublisherModalOpen, setIsPublisherModalOpen] = useState(false);

  // Cover image state
  const [coverImageId, setCoverImageId] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Refs for AI Helper callbacks
  const onAuthorAddedRef = useRef<((authorId: string) => void) | null>(null);
  const onPublisherAddedRef = useRef<((publisherId: string) => void) | null>(
    null,
  );

  // Search states for remote fetching
  const [authorSearch, setAuthorSearch] = useState("");
  const [debouncedAuthorSearch, setDebouncedAuthorSearch] = useState("");
  const [publisherSearch, setPublisherSearch] = useState("");
  const [debouncedPublisherSearch, setDebouncedPublisherSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAuthorSearch(authorSearch), 300);
    return () => clearTimeout(timer);
  }, [authorSearch]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedPublisherSearch(publisherSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [publisherSearch]);

  // Fetch work for editing
  const { data: work, isLoading: workLoading } = useQuery({
    queryKey: ["work", id],
    queryFn: () => worksApi.get(id!),
    enabled: isEditing,
  });

  // Fetch languages
  const { data: languages } = useQuery({
    queryKey: ["languages-all"],
    queryFn: () => languagesApi.list({ page_size: 1000 }),
  });

  // Fetch literary genres
  const { data: literaryGenres } = useQuery({
    queryKey: ["literary-genres-all"],
    queryFn: () => literaryGenresApi.list({ page_size: 1000 }),
  });

  // Fetch authors with remote search
  const { data: authorsData, isLoading: authorsLoading } = useQuery({
    queryKey: ["authors-search", debouncedAuthorSearch],
    queryFn: () =>
      authorsApi.list({ search: debouncedAuthorSearch, page_size: 50 }),
  });

  // Merge search results with the currently selected authors
  const authorOptions = useMemo(() => {
    const options = (authorsData?.items || []).map((a) => ({
      value: a.id,
      label: a.name,
    }));

    // Add currently selected authors if they're not in the search results
    const currentAuthors = work?.book_details?.authors || [];
    currentAuthors.forEach((author) => {
      if (!options.find((o) => o.value === author.id)) {
        options.push({ value: author.id, label: author.name });
      }
    });

    return options;
  }, [authorsData, work]);

  // Fetch publishers with remote search
  const { data: publishersData, isLoading: publishersLoading } = useQuery({
    queryKey: ["publishers-search", debouncedPublisherSearch],
    queryFn: () =>
      publishersApi.list({ search: debouncedPublisherSearch, page_size: 50 }),
  });

  // Merge search results with the currently selected publisher
  const publisherOptions = useMemo(() => {
    const options = (publishersData?.items || []).map((p) => ({
      value: p.id,
      label: p.name,
    }));

    const currentPublisher = work?.book_details?.publisher_obj;
    if (
      currentPublisher &&
      !options.find((o) => o.value === currentPublisher.id)
    ) {
      options.push({
        value: currentPublisher.id,
        label: currentPublisher.name,
      });
    }

    return options;
  }, [publishersData, work]);

  // Fetch nationalities
  const { data: nationalities } = useQuery({
    queryKey: ["nationalities-all"],
    queryFn: () => nationalitiesApi.list({ page_size: 1000 }),
  });

  // Set form values when editing
  useEffect(() => {
    if (work) {
      form.setFieldsValue({
        title: work.title,
        original_title: work.original_title,
        original_language_id: work.original_language_id,
        release_year: work.release_year,
        description: work.description,
        external_url: work.external_url,
        // Book details
        isbn_10: work.book_details?.isbn_10,
        isbn_13: work.book_details?.isbn_13,
        page_count: work.book_details?.page_count,
        publisher: work.book_details?.publisher,
        publisher_id: work.book_details?.publisher_id,
        // Extract IDs from author objects
        author_ids: work.book_details?.authors?.map((a) => a.id) || [],
        // Extract IDs from literary genre objects
        literary_genre_ids:
          work.book_details?.literary_genres?.map((g) => g.id) || [],
      });
      // Set cover image if exists
      if (work.cover_image_id && work.cover_image) {
        setCoverImageId(work.cover_image_id);
        setCoverImagePreview(imagesApi.getFileUrl(work.cover_image_id));
      }
    }
  }, [work, form]);

  const createMutation = useMutation({
    mutationFn: (data: WorkCreate) => worksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });
      message.success("Książka utworzona pomyślnie");
      navigate("/books");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się utworzyć";
      message.error(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: WorkUpdate) => worksApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });
      queryClient.invalidateQueries({ queryKey: ["work", id] });
      message.success("Książka zaktualizowana pomyślnie");
      navigate("/books");
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const createAuthorMutation = useMutation({
    mutationFn: (data: { name: string; nationality_id?: string }) =>
      authorsApi.create(data),
    onSuccess: (newAuthor) => {
      queryClient.invalidateQueries({ queryKey: ["authors-all"] });
      const currentAuthors = form.getFieldValue("author_ids") || [];
      form.setFieldValue("author_ids", [...currentAuthors, newAuthor.id]);
      if (onAuthorAddedRef.current) {
        onAuthorAddedRef.current(newAuthor.id);
      }
      message.success("Autor dodany pomyślnie");
      setIsAuthorModalOpen(false);
      authorForm.resetFields();
    },
    onError: () => message.error("Nie udało się dodać autora"),
  });

  // Mutation for creating new publisher
  const createPublisherMutation = useMutation({
    mutationFn: (data: { name: string }) => publishersApi.create(data),
    onSuccess: (newPublisher) => {
      queryClient.invalidateQueries({ queryKey: ["publishers-all"] });
      form.setFieldValue("publisher_id", newPublisher.id);
      if (onPublisherAddedRef.current) {
        onPublisherAddedRef.current(newPublisher.id);
      }
      message.success("Wydawnictwo dodane pomyślnie");
      setIsPublisherModalOpen(false);
      publisherForm.resetFields();
    },
    onError: () => message.error("Nie udało się dodać wydawnictwa"),
  });

  const onFinish = (values: Record<string, unknown>) => {
    if (isReadOnly) return;

    const payload: WorkCreate | WorkUpdate = {
      type: "book",
      title: values.title as string,
      original_title: values.original_title as string | undefined,
      original_language_id: values.original_language_id as string | undefined,
      release_year: values.release_year as number | undefined,
      description: values.description as string | undefined,
      external_url: values.external_url as string | undefined,
      cover_image_id: coverImageId || undefined,
      book_details: {
        isbn_10: values.isbn_10 as string | undefined,
        isbn_13: values.isbn_13 as string | undefined,
        page_count: values.page_count as number | undefined,
        publisher: values.publisher as string | undefined,
        publisher_id: values.publisher_id as string | undefined,
        author_ids: values.author_ids as string[] | undefined,
        literary_genre_ids: values.literary_genre_ids as string[] | undefined,
      },
    };

    if (isEditing) {
      updateMutation.mutate(payload as WorkUpdate);
    } else {
      createMutation.mutate(payload as WorkCreate);
    }
  };

  if (isEditing && workLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" tip="Ładowanie..." />
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleAIDataAccept = async (
    bookInfo: BookInfoResponse,
    addedAuthorIds: string[],
    addedPublisherId?: string,
  ) => {
    const languageId = languages?.items.find(
      (lang) => lang.code === bookInfo.language_code,
    )?.id;

    const allAuthorIds = [...(bookInfo.author_ids || []), ...addedAuthorIds];
    const finalPublisherId = addedPublisherId || bookInfo.publisher_id;

    // Upload cover image from URL if provided
    if (bookInfo.cover_url) {
      try {
        setIsUploadingImage(true);
        const uploadedImage = await imagesApi.uploadFromUrl(
          bookInfo.cover_url,
          bookInfo.title || undefined,
        );
        setCoverImageId(uploadedImage.id);
        setCoverImagePreview(imagesApi.getFileUrl(uploadedImage.id));
        message.success("Okładka pobrana pomyślnie");
      } catch (error) {
        console.error("Failed to upload cover image:", error);
        message.warning("Nie udało się pobrać okładki");
      } finally {
        setIsUploadingImage(false);
      }
    }

    const formValues: Record<string, any> = {
      title: bookInfo.title || undefined,
      original_title: bookInfo.original_title || undefined,
      release_year: bookInfo.release_year || undefined,
      description: bookInfo.description || undefined,
      original_language_id: languageId || undefined,
      isbn_10: bookInfo.isbn_10 || undefined,
      isbn_13: bookInfo.isbn_13 || undefined,
      page_count: bookInfo.page_count || undefined,
      author_ids: allAuthorIds,
      literary_genre_ids: bookInfo.literary_genre_ids || [],
    };

    if (finalPublisherId) {
      formValues.publisher_id = finalPublisherId;
    }

    form.setFieldsValue(formValues);
  };

  const handleAddMissingAuthor = (authorName: string) => {
    authorForm.setFieldsValue({ name: authorName });
    setIsAuthorModalOpen(true);
  };

  const handleAddMissingPublisher = (publisherName: string) => {
    publisherForm.setFieldsValue({ name: publisherName });
    setIsPublisherModalOpen(true);
  };

  const handleAuthorAdded = (callback: (authorId: string) => void) => {
    onAuthorAddedRef.current = callback;
  };

  const handlePublisherAdded = (callback: (publisherId: string) => void) => {
    onPublisherAddedRef.current = callback;
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          {isEditing
            ? isReadOnly
              ? "Podgląd Książki"
              : "Edytuj Książkę"
            : "Dodaj Książkę"}
        </Title>
      </div>

      <Card>
        {/* AI Helper - only for new works and if not read only */}
        {!isEditing && (
          <AIBookHelper
            onAccept={handleAIDataAccept}
            onAddMissingAuthor={handleAddMissingAuthor}
            onAddMissingPublisher={handleAddMissingPublisher}
            onAuthorAdded={handleAuthorAdded}
            onPublisherAdded={handlePublisherAdded}
          />
        )}

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* Common Fields */}
          {isEditing && work?.slug && (
            <Form.Item label="Slug (URL)">
              <Typography.Text code copyable>
                {work.slug}
              </Typography.Text>
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Tytuł"
                rules={[{ required: true, message: "Tytuł jest wymagany" }]}
              >
                <Input placeholder="Wprowadź tytuł" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="original_title" label="Tytuł oryginalny">
                <Input placeholder="Oryginalny tytuł (jeśli inny)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="release_year" label="Rok wydania">
                <InputNumber
                  min={1800}
                  max={2100}
                  placeholder="e.g. 2024"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="original_language_id" label="Język oryginalny">
                <Select placeholder="Wybierz język" allowClear>
                  {languages?.items.map((lang) => (
                    <Select.Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Okładka">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {coverImagePreview && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src={coverImagePreview}
                        alt="Okładka"
                        style={{
                          maxWidth: 150,
                          maxHeight: 200,
                          objectFit: "contain",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                        }}
                      />
                      {!isReadOnly && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          style={{ position: "absolute", top: 4, right: 4 }}
                          onClick={() => {
                            setCoverImageId(null);
                            setCoverImagePreview(null);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {!isReadOnly && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        try {
                          setIsUploadingImage(true);
                          const uploadedImage = await imagesApi.upload(file);
                          setCoverImageId(uploadedImage.id);
                          setCoverImagePreview(
                            imagesApi.getFileUrl(uploadedImage.id),
                          );
                          message.success("Okładka przesłana pomyślnie");
                        } catch (error) {
                          console.error("Failed to upload image:", error);
                          message.error("Nie udało się przesłać okładki");
                        } finally {
                          setIsUploadingImage(false);
                        }
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={isUploadingImage}
                      >
                        {coverImagePreview
                          ? "Zmień okładkę"
                          : "Wybierz okładkę"}
                      </Button>
                    </Upload>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="external_url" label="Link do źródła">
            <Input placeholder="np. https://lubimyczytac.pl/ksiazka/..." />
          </Form.Item>

          <Form.Item name="description" label="Opis">
            <TextArea rows={4} placeholder="Wprowadź opis..." />
          </Form.Item>

          {/* Book-specific fields */}
          <Divider>Szczegóły książki</Divider>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="isbn_10" label="ISBN-10">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="isbn_13" label="ISBN-13">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="page_count" label="Liczba stron">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="publisher_id" label="Wydawnictwo">
                <Select
                  placeholder="Wybierz wydawnictwo"
                  allowClear
                  showSearch
                  style={{ width: "100%" }}
                  onSearch={setPublisherSearch}
                  filterOption={false}
                  loading={publishersLoading}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {!isReadOnly && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => setIsPublisherModalOpen(true)}
                              block
                            >
                              Dodaj nowe wydawnictwo
                            </Button>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                >
                  {publisherOptions.map((pub) => (
                    <Select.Option key={pub.value} value={pub.value}>
                      {pub.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="author_ids" label="Pisarze">
                <Select
                  mode="multiple"
                  placeholder="Wybierz autorów"
                  allowClear
                  showSearch
                  onSearch={setAuthorSearch}
                  filterOption={false}
                  loading={authorsLoading}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {!isReadOnly && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => setIsAuthorModalOpen(true)}
                              block
                            >
                              Dodaj nowego autora
                            </Button>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                >
                  {authorOptions.map((author) => (
                    <Select.Option key={author.value} value={author.value}>
                      {author.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="literary_genre_ids" label="Gatunki literackie">
                <Select
                  mode="multiple"
                  placeholder="Wybierz gatunki literackie"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {literaryGenres?.items.map((genre) => (
                    <Select.Option key={genre.id} value={genre.id}>
                      {genre.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Actions */}
          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate(-1)}>
                {isReadOnly ? "Wróć" : "Anuluj"}
              </Button>
              {!isReadOnly && (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSubmitting}
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
              {isReadOnly && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  disabled
                  title="Brak uprawnień do edycji"
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Additional Images - only in edit mode */}
      {isEditing && id && (
        <WorkImagesManager workId={id} readOnly={isReadOnly} />
      )}

      {/* Modal for adding new author */}
      <Modal
        title="Dodaj nowego autora"
        open={isAuthorModalOpen}
        onCancel={() => {
          setIsAuthorModalOpen(false);
          authorForm.resetFields();
        }}
        onOk={() => authorForm.submit()}
        confirmLoading={createAuthorMutation.isPending}
        okText="Dodaj"
        cancelText="Anuluj"
      >
        <Form
          form={authorForm}
          layout="vertical"
          onFinish={(values) => createAuthorMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="Imię i nazwisko"
            rules={[
              { required: true, message: "Imię i nazwisko jest wymagane" },
            ]}
          >
            <Input placeholder="np. Andrzej Sapkowski" />
          </Form.Item>
          <Form.Item name="nationality_id" label="Narodowość">
            <Select
              placeholder="Wybierz narodowość"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {nationalities?.items.map((nat) => (
                <Select.Option key={nat.id} value={nat.id}>
                  {nat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for adding new publisher */}
      <Modal
        title="Dodaj nowe wydawnictwo"
        open={isPublisherModalOpen}
        onCancel={() => {
          setIsPublisherModalOpen(false);
          publisherForm.resetFields();
        }}
        onOk={() => publisherForm.submit()}
        confirmLoading={createPublisherMutation.isPending}
        okText="Dodaj"
        cancelText="Anuluj"
      >
        <Form
          form={publisherForm}
          layout="vertical"
          onFinish={(values) => createPublisherMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="Nazwa wydawnictwa"
            rules={[
              { required: true, message: "Nazwa wydawnictwa jest wymagana" },
            ]}
          >
            <Input placeholder="np. SuperNOWA" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BookFormPage;
